import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { AddressAutocomplete, type AddressDetails } from '@/components/ui/address-autocomplete'
import { useToast } from '@/hooks/use-toast'
import {
  useCreateQuote,
  useUsers,
  useAccounts,
  useOpportunities,
  useContacts,
} from '@/hooks/use-api'
import { usePicklistOptions } from '@/hooks/use-picklists'
import {
  QUOTE_REGION_OPTIONS,
  expectedCurrencyForRegion,
  isRegionCurrencyMismatch,
  regionCurrencyWarning,
} from '@/lib/quoteRegions'
import { useUser } from '@/contexts/UserContext'
import { RecordCombobox } from '@/components/ui/record-combobox'
import { useDebounce } from '@/hooks/use-debounce'
import type { Quote } from '@/types'
import { explainError } from '@/lib/explain-error'

interface CreateQuoteModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateQuoteModal({ open, onOpenChange }: CreateQuoteModalProps) {
  const { toast } = useToast()
  const { currentUser } = useUser()
  const createMutation = useCreateQuote()
  const { data: usersData } = useUsers({ limit: 10000 })
  const { data: accountsData } = useAccounts({ limit: 10000 })
  const [oppSearch, setOppSearch] = useState('')
  const debouncedOppSearch = useDebounce(oppSearch, 300)
  const { data: opportunitiesData, isFetching: oppsLoading } = useOpportunities({
    search: debouncedOppSearch || undefined,
    limit: 10000,
    sort_by: 'name',
    sort_order: 'asc',
  } as any)
  const { data: contactsData } = useContacts({ limit: 10000, status__c: 'Active' })
  const quoteStatuses = usePicklistOptions('quote', 'status')
  const currencyCodes = usePicklistOptions('general', 'currency_code')

  const [formData, setFormData] = useState<Partial<Quote>>({
    name: '',
    status: 'Draft',
    owner_id: currentUser?.id || '',
    total_price: 0,
    opportunity_id: '',
    account_id: '',
    expiration_date: '',
    // Quotes issue from the Canadian entity unless the rep picks another. Pairs
    // with the CAD default below, so the two agree and no mismatch warning fires.
    region: 'canada',
    description: '',
    currency_iso_code: 'CAD',
    // New fields
    is_syncing: false,
    option__c: 'Option 1',
    of_files__c: undefined,
    additional_details__c: '',
    discount: undefined,
    tax: undefined,
    shipping_handling: undefined,
    contact_id: '',
    email: '',
    phone: '',
    bill_to_name: '',
    billing_street: '',
    billing_city: '',
    billing_state: '',
    billing_postal_code: '',
    billing_country: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  // The currency should match the issuing entity by default; a deliberate
  // mismatch is allowed but has to be acknowledged before saving.
  const [currencyMismatchAck, setCurrencyMismatchAck] = useState(false)
  const regionCurrencyMismatch = isRegionCurrencyMismatch(
    formData.region,
    formData.currency_iso_code
  )

  const users = usersData?.data || []
  const accounts = accountsData?.data || []
  const opportunities = opportunitiesData?.data || []
  const contacts = contactsData?.data || []

  // Auto-populate email/phone when contact is selected
  useEffect(() => {
    if (formData.contact_id) {
      const selectedContact = contacts.find((c) => c.id === formData.contact_id)
      if (selectedContact) {
        setFormData((prev) => ({
          ...prev,
          email: selectedContact.email || prev.email,
          phone: selectedContact.phone || prev.phone,
        }))
      }
    }
  }, [formData.contact_id, contacts])

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name?.trim()) {
      newErrors.name = 'Quote name is required'
    }

    if (!formData.status) {
      newErrors.status = 'Status is required'
    }

    if (!formData.owner_id) {
      newErrors.owner_id = 'Owner is required'
    }

    if (
      formData.total_price !== undefined &&
      formData.total_price !== null &&
      formData.total_price < 0
    ) {
      newErrors.total_price = 'Total price cannot be negative'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (regionCurrencyMismatch && !currencyMismatchAck) {
      toast({
        title: 'Confirm the currency override',
        description:
          'The quote currency does not match the issuing region. Tick the confirmation box to continue.',
        variant: 'destructive',
      })
      return
    }

    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        variant: 'destructive',
      })
      return
    }

    try {
      const quoteData = {
        name: formData.name,
        status: formData.status,
        owner_id: formData.owner_id,
        total_price: Number(formData.total_price),
        opportunity_id: formData.opportunity_id || undefined,
        account_id: formData.account_id || undefined,
        expiration_date: formData.expiration_date || undefined,
        description: formData.description || undefined,
        currency_iso_code: formData.currency_iso_code || 'CAD',
        // New fields
        is_syncing: formData.is_syncing,
        option__c: formData.option__c || undefined,
        of_files__c: formData.of_files__c,
        additional_details__c: formData.additional_details__c || undefined,
        discount: formData.discount,
        tax: formData.tax,
        shipping_handling: formData.shipping_handling,
        contact_id: formData.contact_id || undefined,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        bill_to_name: formData.bill_to_name || undefined,
        billing_street: formData.billing_street || undefined,
        billing_city: formData.billing_city || undefined,
        billing_state: formData.billing_state || undefined,
        billing_postal_code: formData.billing_postal_code || undefined,
        billing_country: formData.billing_country || undefined,
        // '' means auto-resolve from the account country; send undefined so the
        // backend stores NULL rather than an empty string.
        region: formData.region || undefined,
      }

      await createMutation.mutateAsync(quoteData)

      toast({
        title: 'Success',
        description: 'Quote created successfully.',
      })

      // Reset form and close modal
      setFormData({
        name: '',
        status: 'Draft',
        owner_id: currentUser?.id || '',
        total_price: 0,
        opportunity_id: '',
        account_id: '',
        expiration_date: '',
        region: 'canada',
        description: '',
        currency_iso_code: 'CAD',
        is_syncing: false,
        option__c: 'Option 1',
        of_files__c: undefined,
        additional_details__c: '',
        discount: undefined,
        tax: undefined,
        shipping_handling: undefined,
        contact_id: '',
        email: '',
        phone: '',
        bill_to_name: '',
        billing_street: '',
        billing_city: '',
        billing_state: '',
        billing_postal_code: '',
        billing_country: '',
      })
      setErrors({})
      setCurrencyMismatchAck(false)
      onOpenChange(false)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to create quote')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value }
      // Picking a region moves the currency to that entity's default; the user
      // can still change the currency afterwards (and then acknowledge it).
      if (field === 'region') {
        const expected = expectedCurrencyForRegion(value)
        if (expected) next.currency_iso_code = expected
      }
      return next
    })
    // Any change to either field invalidates a previous acknowledgement.
    if (field === 'region' || field === 'currency_iso_code') setCurrencyMismatchAck(false)
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const selectClassName =
    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        aria-describedby="create-quote-description"
      >
        <DialogHeader>
          <DialogTitle>Create New Quote</DialogTitle>
          <DialogDescription id="create-quote-description">
            Fill in the details to create a new quote. Fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Quote Information Section */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 bg-muted px-3 py-1.5 rounded-sm">
              Quote Information
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Quote Name */}
              <div>
                <Label htmlFor="name">Quote Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Enter quote name"
                  aria-required="true"
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                />
                {errors.name && (
                  <p id="name-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Expiration Date */}
              <div>
                <Label htmlFor="expiration_date">Expiration Date</Label>
                <DatePicker
                  id="expiration_date"
                  value={formData.expiration_date}
                  onChange={(v) => handleChange('expiration_date', v ?? '')}
                  aria-label="Expiration date"
                />
              </div>

              {/* Opportunity */}
              <div>
                <Label htmlFor="opportunity_id">Opportunity Name</Label>
                <RecordCombobox
                  id="opportunity_id"
                  records={opportunities}
                  value={formData.opportunity_id as string}
                  onValueChange={(value) => handleChange('opportunity_id', value)}
                  placeholder="Select opportunity..."
                  searchPlaceholder="Search opportunities..."
                  emptyMessage="No opportunities found."
                  displayField={(opp) => opp.name || opp.opportunity_name || ''}
                  aria-label="Opportunity Name"
                  onSearchChange={setOppSearch}
                  isLoading={oppsLoading}
                  totalCount={opportunitiesData?.total}
                />
              </div>

              {/* Primary Quote Checkbox */}
              <div className="flex items-center space-x-2 pt-6">
                <input
                  id="is_syncing"
                  type="checkbox"
                  checked={formData.is_syncing === true}
                  onChange={(e) => handleChange('is_syncing', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  aria-label="Primary Quote"
                />
                <Label htmlFor="is_syncing" className="text-sm font-normal cursor-pointer">
                  Primary Quote
                </Label>
              </div>

              {/* Status */}
              <div>
                <Label htmlFor="status">Status *</Label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className={selectClassName}
                  aria-required="true"
                  aria-invalid={!!errors.status}
                  aria-describedby={errors.status ? 'status-error' : undefined}
                >
                  {quoteStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
                {errors.status && (
                  <p id="status-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.status}
                  </p>
                )}
              </div>

              {/* Account */}
              <div>
                <Label htmlFor="account_id">Account Name</Label>
                <select
                  id="account_id"
                  value={formData.account_id}
                  onChange={(e) => handleChange('account_id', e.target.value)}
                  className={selectClassName}
                  aria-label="Account"
                >
                  <option value="">Select account...</option>
                  {[...accounts]
                    .sort((a, b) =>
                      (a.name || a.account_name || '').localeCompare(b.name || b.account_name || '')
                    )
                    .map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name || account.account_name}
                      </option>
                    ))}
                </select>
              </div>

              {/* Quote Currency */}
              <div>
                <Label htmlFor="currency_iso_code">Quote Currency *</Label>
                <select
                  id="currency_iso_code"
                  value={formData.currency_iso_code}
                  onChange={(e) => handleChange('currency_iso_code', e.target.value)}
                  className={selectClassName}
                  aria-label="Quote currency"
                >
                  {currencyCodes.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              </div>

              {/* Region — which GrackleDocs entity the quote is issued from.
                  Defaults to Canada. There is no "auto" choice here: a new quote
                  always names its entity, so the legal entity printed on a
                  customer-facing PDF is never decided implicitly at render time.
                  (Quotes predating this still hold a blank region and keep
                  resolving by account country — see QuoteDetail's edit form.) */}
              <div>
                <Label htmlFor="region">Issuing Region</Label>
                <select
                  id="region"
                  value={formData.region || 'canada'}
                  onChange={(e) => handleChange('region', e.target.value)}
                  className={selectClassName}
                  aria-label="Issuing region (company entity shown on the quote)"
                  aria-describedby="region-help"
                >
                  {QUOTE_REGION_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <p id="region-help" className="text-xs text-muted-foreground mt-1">
                  Sets the company name and address printed on the quote.
                </p>
              </div>

              {/* Currency / region mismatch — allowed, but must be deliberate. */}
              {regionCurrencyMismatch && (
                <div
                  className="md:col-span-2 rounded-md border border-destructive/50 bg-destructive/5 p-3"
                  role="alert"
                >
                  <p className="text-sm font-medium text-destructive">
                    Currency does not match the issuing region
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {regionCurrencyWarning(formData.region, formData.currency_iso_code)}
                  </p>
                  <label className="flex items-start gap-2 mt-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={currencyMismatchAck}
                      onChange={(e) => setCurrencyMismatchAck(e.target.checked)}
                      aria-describedby="region-currency-ack-help"
                    />
                    <span id="region-currency-ack-help">
                      I understand and want to use{' '}
                      <span className="font-medium">{formData.currency_iso_code}</span> for this
                      quote.
                    </span>
                  </label>
                </div>
              )}

              {/* Option - hidden for now, may re-enable later */}

              {/* # of Files */}
              <div>
                <Label htmlFor="of_files"># of Files</Label>
                <Input
                  id="of_files"
                  type="number"
                  min="0"
                  value={formData.of_files__c ?? ''}
                  onChange={(e) =>
                    handleChange(
                      'of_files__c',
                      e.target.value ? parseInt(e.target.value) : undefined
                    )
                  }
                  placeholder="0"
                  aria-label="Number of files"
                />
              </div>

              {/* Owner */}
              <div>
                <Label htmlFor="owner_id">Owner *</Label>
                <select
                  id="owner_id"
                  value={formData.owner_id}
                  onChange={(e) => handleChange('owner_id', e.target.value)}
                  className={selectClassName}
                  aria-required="true"
                  aria-invalid={!!errors.owner_id}
                  aria-describedby={errors.owner_id ? 'owner_id-error' : undefined}
                >
                  <option value="">Select owner...</option>
                  {[...users]
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                </select>
                {errors.owner_id && (
                  <p id="owner_id-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.owner_id}
                  </p>
                )}
              </div>
            </div>

            {/* Description - full width */}
            <div className="mt-4">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Enter quote description"
                rows={3}
              />
            </div>

            {/* Additional Details - full width */}
            <div className="mt-4">
              <Label htmlFor="additional_details">Additional Details</Label>
              <Textarea
                id="additional_details"
                value={formData.additional_details__c}
                onChange={(e) => handleChange('additional_details__c', e.target.value)}
                placeholder="Enter additional details..."
                rows={3}
              />
            </div>
          </div>

          {/* Totals Section */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 bg-muted px-3 py-1.5 rounded-sm">
              Totals
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Total Price */}
              <div>
                <Label htmlFor="total_price">Subtotal / Estimated Total</Label>
                <Input
                  id="total_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.total_price}
                  onChange={(e) => handleChange('total_price', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  aria-describedby="total_price-help"
                />
                <p id="total_price-help" className="text-sm text-muted-foreground mt-1">
                  Leave at 0 to calculate from line items later
                </p>
              </div>

              {/* Discount */}
              <div>
                <Label htmlFor="discount">Discount (%)</Label>
                <Input
                  id="discount"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.discount ?? ''}
                  onChange={(e) =>
                    handleChange(
                      'discount',
                      e.target.value ? parseFloat(e.target.value) : undefined
                    )
                  }
                  placeholder="0"
                  aria-label="Discount percentage"
                />
              </div>

              {/* Tax */}
              <div>
                <Label htmlFor="tax">Tax</Label>
                <Input
                  id="tax"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.tax ?? ''}
                  onChange={(e) =>
                    handleChange('tax', e.target.value ? parseFloat(e.target.value) : undefined)
                  }
                  placeholder="0.00"
                  aria-label="Tax amount"
                />
              </div>

              {/* Shipping & Handling */}
              <div>
                <Label htmlFor="shipping_handling">Shipping & Handling</Label>
                <Input
                  id="shipping_handling"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.shipping_handling ?? ''}
                  onChange={(e) =>
                    handleChange(
                      'shipping_handling',
                      e.target.value ? parseFloat(e.target.value) : undefined
                    )
                  }
                  placeholder="0.00"
                  aria-label="Shipping and handling cost"
                />
              </div>

              {/* Calculated Grand Total - display only */}
              <div className="col-span-2">
                <Label>Grand Total (Calculated)</Label>
                <div
                  className="px-3 py-2 border rounded-md bg-muted/30 font-semibold text-lg"
                  aria-label="Calculated grand total"
                >
                  $
                  {(
                    (formData.total_price || 0) +
                    (formData.tax || 0) +
                    (formData.shipping_handling || 0)
                  ).toFixed(2)}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Subtotal + Tax + Shipping & Handling
                </p>
              </div>
            </div>
          </div>

          {/* Prepared For Section */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 bg-muted px-3 py-1.5 rounded-sm">
              Prepared For
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Contact Name */}
              <div>
                <Label htmlFor="contact_id">Contact Name</Label>
                <select
                  id="contact_id"
                  value={formData.contact_id}
                  onChange={(e) => handleChange('contact_id', e.target.value)}
                  className={selectClassName}
                  aria-label="Contact"
                >
                  <option value="">Select contact...</option>
                  {[...contacts]
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((contact) => (
                      <option key={contact.id} value={contact.id}>
                        {contact.name}
                      </option>
                    ))}
                </select>
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="Enter phone number"
                  aria-label="Phone"
                />
              </div>

              {/* Email */}
              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="Enter email address"
                  aria-label="Email"
                />
              </div>
            </div>
          </div>

          {/* Address Information Section */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 bg-muted px-3 py-1.5 rounded-sm">
              Address Information
            </h3>

            <div className="space-y-4">
              {/* Bill To Name */}
              <div>
                <Label htmlFor="bill_to_name">Bill To Name</Label>
                <Input
                  id="bill_to_name"
                  value={formData.bill_to_name}
                  onChange={(e) => handleChange('bill_to_name', e.target.value)}
                  placeholder="Enter bill to name"
                  aria-label="Bill to name"
                />
              </div>

              {/* Bill To Street with Autocomplete */}
              <AddressAutocomplete
                id="billing_address"
                label="Bill To Street"
                value={formData.billing_street || ''}
                onAddressSelect={(address: AddressDetails) => {
                  setFormData((prev) => ({
                    ...prev,
                    billing_street: address.street,
                    billing_city: address.city,
                    billing_state: address.state,
                    billing_postal_code: address.postal_code,
                    billing_country: address.country,
                  }))
                }}
                onStreetChange={(street) => handleChange('billing_street', street)}
                placeholder="Start typing address..."
              />

              <div className="grid grid-cols-2 gap-4">
                {/* Bill To City */}
                <div>
                  <Label htmlFor="billing_city">Bill To City</Label>
                  <Input
                    id="billing_city"
                    value={formData.billing_city}
                    onChange={(e) => handleChange('billing_city', e.target.value)}
                    placeholder="City"
                  />
                </div>

                {/* Bill To State/Province */}
                <div>
                  <Label htmlFor="billing_state">Bill To State/Province</Label>
                  <Input
                    id="billing_state"
                    value={formData.billing_state}
                    onChange={(e) => handleChange('billing_state', e.target.value)}
                    placeholder="State"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Bill To Zip/Postal Code */}
                <div>
                  <Label htmlFor="billing_postal_code">Bill To Zip/Postal Code</Label>
                  <Input
                    id="billing_postal_code"
                    value={formData.billing_postal_code}
                    onChange={(e) => handleChange('billing_postal_code', e.target.value)}
                    placeholder="Postal Code"
                  />
                </div>

                {/* Bill To Country */}
                <div>
                  <Label htmlFor="billing_country">Bill To Country</Label>
                  <Input
                    id="billing_country"
                    value={formData.billing_country}
                    onChange={(e) => handleChange('billing_country', e.target.value)}
                    placeholder="Country"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
              aria-label="Cancel and close modal"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending}
              aria-label={createMutation.isPending ? 'Creating quote...' : 'Create quote'}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Quote'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
